import React from "react";
import { Modal } from "components/ui/Modal";
import { Button } from "components/ui/Button";
import { getActDetails } from "api/acts";
import { getOrgSettings } from "api/settings";

const DEV_DEBUG = false;

const PrintIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h8a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
      clipRule="evenodd"
    />
  </svg>
);

export default function ActDetailsModal({
  open,
  act,
  onClose,
  typeLabel,
  user,
}) {
  const printRef = React.useRef(null);
  const [rows, setRows] = React.useState([]);
  const [orgData, setOrgData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const log = (...a) => DEV_DEBUG && console.log("[ActDetailsModal]", ...a);
  const money = (v) =>
    (Number(v) || 0).toLocaleString("ru-RU", { minimumFractionDigits: 0 });

  React.useEffect(() => {
    let ignore = false;

    async function load() {
      if (!open || !act) return;
      setLoading(true);
      setErr(null);
      setRows([]);
      setOrgData(null);

      try {
        const actId = act?.id ?? act?.act_id ?? null;
        if (!actId) {
          throw new Error("Akt ID ko'rsatilmagan");
        }

        log("load start", { actId, act_type: act?.act_type });

        // Загружаем данные акта и организации параллельно
        const [actDetails, org] = await Promise.all([
          getActDetails(actId),
          getOrgSettings(),
        ]);

        if (ignore) return;

        // Преобразуем позиции в нужный формат
        const positions = (actDetails.positions || []).map((pos, idx) => {
          const qty = Number(pos.qty) || 0;
          const price = Number(pos.price) || 0;
          return {
            id: pos.id || idx,
            act_id: actId,
            act_no: actDetails.act_no || act?.act_no,
            item_id: pos.item_id,
            item_name: pos.item_name || pos.name || "—",
            item_unit: pos.item_unit || pos.unit || "шт",
            qty: qty,
            price: price,
          };
        });

        setRows(positions);
        setOrgData(org);
      } catch (e) {
        log("load error", e);
        if (!ignore) {
          setErr(e.message || "Akt ma'lumotlarini yuklab bo'lmadi");
          setRows([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [open, act]);

  const handlePrint = () => {
    const html = printRef.current?.innerHTML || "";
    const original = document.body.innerHTML;
    document.body.innerHTML = html;
    window.print();
    document.body.innerHTML = original;
    window.location.reload();
  };

  if (!open) return null;

  const total = rows.reduce(
    (s, r) => s + (Number(r.qty) || 0) * (Number(r.price) || 0),
    0
  );

  const headingByType = (t) => {
    if (t === "out") return "CHIQIM DALOLATNOMASI";
    if (t === "return") return "QAYTARISH DALOLATNOMASI";
    return "KIRIM DALOLATNOMASI";
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Aktni ko'rish"
      className="bg-gray-100 dark:bg-slate-900 max-w-4xl"
    >
      <div className="max-h-[70vh] overflow-y-auto bg-gray-100 dark:bg-slate-900 p-4 printable-area">
        <div ref={printRef} className="bg-white text-black p-12 shadow-lg A4-document">
          {/* Данные организации */}
          {orgData && (
            <div className="mb-8 text-sm">
              {orgData.logo_url && (
                <div className="mb-4 text-center">
                  <img
                    src={orgData.logo_url}
                    alt="Logo"
                    className="h-16 mx-auto object-contain"
                  />
                </div>
              )}
              {orgData.org_name && (
                <h2 className="text-lg font-bold text-center mb-2">
                  {orgData.org_name}
                </h2>
              )}
              <div className="text-center space-y-1 text-gray-700">
                {orgData.inn && <p>ИНН: {orgData.inn}</p>}
                {orgData.address && <p>{orgData.address}</p>}
                {orgData.phone && <p>Тел.: {orgData.phone}</p>}
              </div>
            </div>
          )}

          <header className="text-center mb-10">
            <h1 className="text-2xl font-bold">
              {headingByType(act?.act_type)} № {act?.act_no ?? "—"}
            </h1>
            <p className="text-sm">
              {act?.created_at
                ? new Date(act.created_at).toLocaleDateString("ru-RU")
                : "—"}{" "}
              y.
            </p>
          </header>

          <div className="flex justify-between text-sm mb-8">
            <p>{orgData?.address ? orgData.address.split(',')[0] : "Toshkent sh."}</p>
            <p>
              {act?.created_at
                ? new Date(act.created_at).toLocaleDateString("ru-RU", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
                : "—"}
            </p>
          </div>

          <div className="space-y-4 text-sm mb-8">
            {act?.act_type === "out" && (
              <p>
                Ushbu dalolatnoma shuni tasdiqlaydiki, qabul qiluvchi{" "}
                <span className="font-semibold">
                  {user?.full_name || act?.counterparty || "Qabul qiluvchi"}
                </span>{" "}
                omboridan quyidagi tovar-moddiy boyliklar berildi:
              </p>
            )}
            {act?.act_type === "return" && (
              <p>
                Ushbu dalolatnoma shuni tasdiqlaydiki,{" "}
                <span className="font-semibold">
                  {user?.full_name || act?.counterparty || "Qabul qiluvchi"}
                </span>{" "}
                tomonidan quyidagi tovar-moddiy boyliklar omborga (qaytarish) qabul qilindi:
              </p>
            )}
            {(!act?.act_type || act?.act_type === "in") && (
              <p>
                Ushbu dalolatnoma shuni tasdiqlaydiki, <span className="font-semibold">{act?.counterparty || "Yetkazib beruvchi"}</span> tomonidan <span className="font-semibold">Ombor</span>ga{" "}
                quyidagi tovar-moddiy boyliklar (kirim) qabul qilindi:
              </p>
            )}
          </div>

          {loading && (
            <div className="p-6 text-center text-sm text-gray-500 border rounded-md">
              Pozitsiyalar yuklanmoqda...
            </div>
          )}
          {err && !loading && (
            <div className="p-6 text-center text-sm text-red-600 border rounded-md">
              {err}
            </div>
          )}

          {!loading && !err && (
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border p-2 font-semibold text-left">№</th>
                  <th className="border p-2 font-semibold text-left">
                    Nomi
                  </th>
                  <th className="border p-2 font-semibold text-right">
                    Miqdor
                  </th>
                  <th className="border p-2 font-semibold text-right">
                    Narx (UZS)
                  </th>
                  <th className="border p-2 font-semibold text-right">
                    Summa (UZS)
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="border p-3 text-center text-gray-500"
                    >
                      Ushbu aktda ko'rsatish uchun pozitsiyalar yo'q.
                    </td>
                  </tr>
                ) : (
                  rows.map((pos, i) => {
                    const qty = Number(pos.qty) || 0;
                    const price = pos.price !== null && pos.price !== undefined ? Number(pos.price) : 0;
                    return (
                      <tr key={pos.id ?? i}>
                        <td className="border p-2 text-center">{i + 1}</td>
                        <td className="border p-2">{pos.item_name ?? "—"}</td>
                        <td className="border p-2 text-right">
                          {qty} {pos.item_unit || "шт"}
                        </td>
                        <td className="border p-2 text-right">
                          {money(price)} (UZS)
                        </td>
                        <td className="border p-2 text-right">
                          {money(qty * price)} (UZS)
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} className="border p-2 text-right font-bold">
                    Jami (UZS):
                  </td>
                  <td className="border p-2 text-right font-bold">
                    {money(total)} (UZS)
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          <footer className="mt-16 grid grid-cols-2 gap-16 text-sm">
            <div>
              <p className="font-semibold">Topshirdi:</p>
              <div className="mt-8 border-b border-gray-400"></div>
              <p className="text-xs text-center">(Imzo)</p>
            </div>
            <div>
              <p className="font-semibold">
                {act?.act_type === "return"
                  ? "Qaytardi:"
                  : "Qabul qildi (Qabul qiluvchi):"}
              </p>
              <div className="mt-8 border-b border-gray-400"></div>
              <p className="text-xs text-center">(Imzo)</p>
            </div>
          </footer>
        </div>
      </div>

      <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <Button variant="ghost" onClick={onClose} className="text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700">
          Yopish
        </Button>
        <Button
          onClick={handlePrint}
          className="bg-blue-600 text-white hover:bg-blue-700"
        >
          <PrintIcon /> Chop etish / Yuklab olish
        </Button>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .printable-area, .printable-area * { visibility: visible; }
          .printable-area { position: absolute; left: 0; top: 0; width: 100%; }
          .A4-document { box-shadow: none; border: none; }
        }
      `}</style>
    </Modal>
  );
}
